import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Link, useNavigate } from 'react-router-dom';
import { TrophyIcon, AcademicCapIcon, BookOpenIcon, ListBulletIcon, ChatBubbleLeftRightIcon } from '@heroicons/react/24/outline';
import { useAuth } from '../../contexts/AuthContext';
import useHasDiary from '../../components/diary/useHasDiary';
import { useFeatures } from '../../hooks/useFeature';
import { childPages } from './ChildLayout';
import { pendingCount } from '../../utils/offline';

const ICONS = {
  '/child/level': TrophyIcon,
  '/child/certificates': AcademicCapIcon,
  '/child/diary': BookOpenIcon,
  '/child/routines': ListBulletIcon,
};

const RECENT_MS = 7 * 24 * 60 * 60 * 1000;

// What the coach did with the gymnast's own routines: any sent back, and any
// approved in the last week. Shown on the Routines tile.
function useRoutineNews(gymnastId) {
  const [news, setNews] = useState(null);
  useEffect(() => {
    if (!gymnastId) return;
    axios.get(`/api/gymnast-routines/gymnast/${gymnastId}`).then(res => {
      const mine = (Array.isArray(res.data) ? res.data : []).filter(r => r.submittedByGymnast);
      const sentBack = mine.filter(r => r.pendingVerification && r.sentBackAt).length;
      const approved = mine.filter(r => !r.pendingVerification && r.verifiedAt && Date.now() - new Date(r.verifiedAt) < RECENT_MS).length;
      if (sentBack) setNews({ text: `${sentBack} sent back`, urgent: true });
      else if (approved) setNews({ text: `${approved} approved` });
      else setNews(null);
    }).catch(() => {});
  }, [gymnastId]);
  return news;
}

// Messages: only for 16 and 17-year-olds
// (the server says whether chat is open to them)
function useChat() {
  const [chat, setChat] = useState(null);
  useEffect(() => {
    axios.get('/api/chat/unread-count').then(res => setChat(res.data?.available ? res.data : null)).catch(() => {});
  }, []);
  return chat;
}

// Same welcome and tiles as the parent and coach dashboard.
export default function ChildHome() {
  const { user, logout } = useAuth();
  const has = useFeatures();
  const hasDiary = useHasDiary() && has('diary');
  const routineNews = useRoutineNews(user?.id);
  const chat = useChat();
  const navigate = useNavigate();
  const signOut = () => {
    const waiting = pendingCount();
    if (waiting && !window.confirm(`${waiting} change${waiting === 1 ? " hasn't" : "s haven't"} been sent yet (no signal). They'll be sent next time you sign in on this phone. Sign out anyway?`)) return;
    logout();
    navigate('/child-login');
  };
  return (
    <div className="dashboard">
      <h1 className="dashboard__welcome">Welcome back, {user.firstName}!</h1>
      <nav className="dashboard-tiles" aria-label="Your pages">
        {childPages(hasDiary, has('certificates')).map(p => {
          const Icon = ICONS[p.to];
          return (
            <Link key={p.to} to={p.to} className="dashboard-tile">
              <Icon className="dashboard-tile__icon" aria-hidden="true" />
              <span className="dashboard-tile__label">{p.label}</span>
              {p.to === '/child/routines' && routineNews && (
                <span className={`child-tile-note${routineNews.urgent ? ' child-tile-note--urgent' : ''}`}>{routineNews.text}</span>
              )}
            </Link>
          );
        })}
        {chat && (
          <Link to="/child/messages" className="dashboard-tile">
            <ChatBubbleLeftRightIcon className="dashboard-tile__icon" aria-hidden="true" />
            <span className="dashboard-tile__label">Messages</span>
            {chat.count > 0 && <span className="child-tile-note">{chat.count} new</span>}
          </Link>
        )}
      </nav>
      <button type="button" className="btn btn-outline child-app__signout" onClick={signOut}>Sign out</button>
    </div>
  );
}
